import React, { useEffect, useState, useRef } from "react";
import { API_BASE_URL } from "../api/index";
import { fetchOnboardingState, isOnboardingActive } from "../api/onboarding";
import { persistSession, isEmailNotVerifiedError } from "../utils/auth";
import { setUserProperties, trackSignUp } from "../utils/analytics";
import {
  setMixpanelUserProperties,
  trackUserLogin,
} from "../utils/mixpanel";

export const AuthCallback = () => {
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Processing authentication...");
  const [error, setError] = useState(null);

  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    handleAuthCallback();
  }, []);

  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      code: urlParams.get("code"),
      state: urlParams.get("state"),
      error: urlParams.get("error"),
      errorDescription: urlParams.get("error_description"),
    };
  };

  const handleAuthCallback = async () => {
    try {
      // Get parameters from URL
      const { code, state, error, errorDescription } = getUrlParams();

      // Check for OAuth errors
      if (error) {
        console.error("OAuth error detected:", error, errorDescription);
        throw new Error(errorDescription || `OAuth error: ${error}`);
      }

      // Check for authorization code
      if (!code) {
        console.error("No authorization code found");
        throw new Error("No authorization code received from Google");
      }

      // Validate state parameter (CSRF protection)
      const storedState = sessionStorage.getItem("oauth_state");
      if (state !== storedState) {
        console.error("State mismatch - possible CSRF attack");
        throw new Error("Invalid state parameter - possible CSRF attack");
      }

      setMessage("Exchanging authorization code for tokens...");

      // Exchange authorization code for tokens
      const requestData = {
        code,
        state,
        redirectUri: window.location.origin + window.location.pathname,
      };

      const tokenResponse = await fetch(
        `${API_BASE_URL}/auth/google/callback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        },
      );

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        console.error("Backend error response:", errorData);
        throw new Error(
          errorData.message || "Failed to exchange authorization code",
        );
      }

      const tokenData = await tokenResponse.json();
      setMessage("Fetching user profile...");

      // The backend should return user info and tokens
      const { user, accessToken, refreshToken, expiresIn } = tokenData;

      if (!user || !user.userId) {
        throw new Error("Invalid user data received from server");
      }

      if (!accessToken) {
        console.error("No accessToken received from backend:", tokenData);
        throw new Error("No access token received from server");
      }

      persistSession({
        user,
        accessToken,
        expiresIn,
      });

      // Set user properties for analytics
      setUserProperties(user.userId);

      if (user.isNewUser) {
        trackSignUp("google");
      }

      // Also set user properties in Mixpanel
      try {
        setMixpanelUserProperties(user.userId, {
          email: user.email,
          name: user.name,
          signup_date: new Date().toISOString(),
          auth_method: "google",
        });

        // Track successful registration/login in Mixpanel
        // Check if this is a new user or existing user
        if (!user.isNewUser) {
          // Existing user login
          trackUserLogin(user.userId, "google", {
            email: user.email,
            name: user.name,
          });
        }
      } catch (error) {
        console.error("Error setting Mixpanel user properties:", error);
      }

      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      window.authData = {
        userId: user.userId,
        userData: user,
        accessToken,
        refreshToken,
        tokenExpiry: expiresIn ? Date.now() + expiresIn * 1000 : null,
      };

      // Clean up OAuth state
      sessionStorage.removeItem("oauth_state");
      sessionStorage.removeItem("oauth_provider");

      setStatus("success");
      setMessage("Authentication successful! Redirecting...");

      // Resolve destination: explicit intended_route wins; otherwise ask the
      // backend for the user's onboarding state and pick /setup or /dashboard.
      const intendedRoute = sessionStorage.getItem("intended_route");
      sessionStorage.removeItem("intended_route");

      let destination = intendedRoute;
      if (!destination) {
        try {
          const result = await fetchOnboardingState(user.userId);
          if (!result.ok) {
            if (isEmailNotVerifiedError(result.error)) {
              destination = "/verify-email";
            } else {
              console.warn(
                "Onboarding state fetch failed; deferring routing to ProtectedRoute",
                result.error,
              );
              destination = "/dashboard";
            }
          } else if (isOnboardingActive(result.value)) {
            destination = "/setup";
          } else {
            destination = "/dashboard";
          }
        } catch (resolveErr) {
          console.warn(
            "Failed to resolve onboarding destination, defaulting to /dashboard",
            resolveErr,
          );
          destination = "/dashboard";
        }
      }

      setTimeout(() => {
        window.location.href = destination;
      }, 1500);
    } catch (err) {
      console.error("Auth callback error:", err);
      setStatus("error");
      setError(err.message);
      setMessage("Authentication failed.");

      // Clean up any stored OAuth data
      sessionStorage.removeItem("oauth_state");
      sessionStorage.removeItem("oauth_provider");

      // Redirect to login page after delay
      setTimeout(() => {
        window.location.href = "/signup";
      }, 3000);
    }
  };

  const handleRetry = () => {
    // Clear error state and redirect to login
    window.location.href = "/signup";
  };

  return (
    <div className="auth-callback">
      <div className="auth-callback-container">
        <div className="auth-callback-content">
          {status === "processing" && (
            <>
              <div className="spinner"></div>
              <h2>Authenticating...</h2>
              <p>{message}</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="success-icon">✅</div>
              <h2>Authentication Successful!</h2>
              <p>{message}</p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="error-icon">❌</div>
              <h2>Authentication Failed</h2>
              <p className="error-message">{error}</p>
              <button onClick={handleRetry} className="retry-button">
                Try Again
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        .auth-callback {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .auth-callback-container {
          max-width: 400px;
          width: 90%;
          padding: 20px;
        }

        .auth-callback-content {
          background: white;
          border-radius: 12px;
          padding: 40px 30px;
          text-align: center;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .success-icon,
        .error-icon {
          font-size: 48px;
          margin-bottom: 20px;
        }

        h2 {
          color: #333;
          margin: 0 0 15px 0;
          font-size: 24px;
          font-weight: 600;
        }

        p {
          color: #666;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }

        .error-message {
          color: #dc3545;
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          border-radius: 6px;
          padding: 12px;
          margin: 15px 0 20px 0;
          font-size: 14px;
        }

        .retry-button {
          background: #667eea;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .retry-button:hover {
          background: #5a67d8;
          transform: translateY(-1px);
        }

        .retry-button:active {
          transform: translateY(0);
        }

        @media (max-width: 480px) {
          .auth-callback-content {
            padding: 30px 20px;
          }

          h2 {
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};
